import { api } from "@shared/api/client";
import type {
  ManualSistemaIntegradoControlModificacion,
  ManualSistemaIntegradoControlPayload,
  ManualSistemaIntegradoState,
  ManualSistemaIntegradoStructuredSection,
  ManualSistemaIntegradoStructuredSectionPayload,
  ManualSistemaIntegradoStructuredState,
  ManualSistemaIntegradoUploadPayload,
} from "../types/manual-sistema-integrado";

const BASE_URL = "/certificaciones/manual-sistema-integrado";

export const manualSistemaIntegradoApi = {
  async getState(): Promise<ManualSistemaIntegradoState> {
    const { data } = await api.get<ManualSistemaIntegradoState>(BASE_URL);
    return data;
  },

  async getPreview(): Promise<Blob> {
    const { data } = await api.get<Blob>(`${BASE_URL}/preview`, {
      responseType: "blob",
    });
    return data;
  },

  async upload(payload: ManualSistemaIntegradoUploadPayload): Promise<void> {
    const formData = new FormData();
    formData.append("documento", payload.documento);
    formData.append("edicion", payload.edicion);
    formData.append("fecha_edicion", payload.fecha_edicion);
    formData.append("modificaciones_realizadas", payload.modificaciones_realizadas);

    await api.post(BASE_URL, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },

  async download(): Promise<Blob> {
    const { data } = await api.get<Blob>(`${BASE_URL}/download`, {
      responseType: "blob",
    });
    return data;
  },

  async listControlDocumental(): Promise<ManualSistemaIntegradoControlModificacion[]> {
    const { data } = await api.get<{
      data: ManualSistemaIntegradoControlModificacion[];
    }>(`${BASE_URL}/control-documental`);
    return data.data;
  },

  async createControlDocumental(payload: ManualSistemaIntegradoControlPayload): Promise<void> {
    await api.post(`${BASE_URL}/control-documental`, payload);
  },

  async updateControlDocumental(
    id: number,
    payload: ManualSistemaIntegradoControlPayload,
  ): Promise<void> {
    await api.put(`${BASE_URL}/control-documental/${id}`, payload);
  },

  async deleteControlDocumental(id: number): Promise<void> {
    await api.delete(`${BASE_URL}/control-documental/${id}`);
  },

  async getStructured(): Promise<ManualSistemaIntegradoStructuredState> {
    const { data } = await api.get<ManualSistemaIntegradoStructuredState>(`${BASE_URL}/estructurado`);
    return data;
  },

  async createStructuredSection(
    payload: ManualSistemaIntegradoStructuredSectionPayload,
  ): Promise<ManualSistemaIntegradoStructuredSection> {
    const { data } = await api.post<{ data: ManualSistemaIntegradoStructuredSection }>(
      `${BASE_URL}/estructurado/secciones`,
      payload,
    );
    return data.data;
  },

  async updateStructuredSection(
    id: number,
    payload: ManualSistemaIntegradoStructuredSectionPayload,
  ): Promise<ManualSistemaIntegradoStructuredSection> {
    const { data } = await api.put<{ data: ManualSistemaIntegradoStructuredSection }>(
      `${BASE_URL}/estructurado/secciones/${id}`,
      payload,
    );
    return data.data;
  },

  async deleteStructuredSection(id: number): Promise<void> {
    await api.delete(`${BASE_URL}/estructurado/secciones/${id}`);
  },

  async uploadStructuredImage(
    sectionId: number,
    image: File,
  ): Promise<ManualSistemaIntegradoStructuredSection> {
    const formData = new FormData();
    formData.append("imagen", image);
    formData.append("pie", image.name);
    const { data } = await api.post<{ data: ManualSistemaIntegradoStructuredSection }>(
      `${BASE_URL}/estructurado/secciones/${sectionId}/imagenes`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    return data.data;
  },

  async deleteStructuredImage(sectionId: number, imageId: string): Promise<void> {
    await api.delete(`${BASE_URL}/estructurado/secciones/${sectionId}/imagenes/${imageId}`);
  },

  structuredImageUrl(sectionId: number, imageId: string): string {
    const base = api.defaults.baseURL ?? "/api/v1";
    return `${base}${BASE_URL}/estructurado/secciones/${sectionId}/imagenes/${imageId}/preview`;
  },

  async downloadStructuredPdf(): Promise<Blob> {
    const { data } = await api.get<Blob>(`${BASE_URL}/estructurado/pdf`, {
      responseType: "blob",
    });
    return data;
  },
};
